import React from "react";

import AppService from "@service/AppService/AppService";
import { SalesforceFieldService } from "@service/salesforceField/SalesforceFieldService";
import { createBrowserHistory } from "history";
import {
    getSurveyLocation,
    resetSurveyLocation,
    saveSurveyLocation,
    validatePhoneNumber
} from "./requests/surveyAPIRequests/surveyAPIRequests";
import { unansweredQuestions } from "./shared/AnswersTracker/AnswersTracker";
import { AuthContext } from "./AuthContext";
import { UserSurveyStatus } from "./helper/Constants";
import { QuestionListService } from "./service/QuestionListService/QuestionListService";
import { surveyQuestionMethods } from "./shared/Questions/Questions";

export const SurveyQuestionsContext = React.createContext(null);

class SurveyQuestionsStore extends React.Component {
    static contextType = AuthContext;
    constructor(props) {
        super(props);
        this.state = {
            questions: null,
            allSubSurveySections: null,
            subSurveys: null,
            dynamicQuestions: [],
            currentSubSurvey: surveyQuestionMethods.isSurrogateAccountType() && !this.context?.isSurveyFinished() ? 1 : 0,
            finishedSurveys: surveyQuestionMethods.isSurrogateAccountType() && !this.context?.isSurveyFinished() ? [0] : [],
            showSurveys: true,
            lastSectionPassed: 0,
            indexesActive: false,
            isSurveyLoaded: false,
            accountType: AppService.accountType,
            stopScrolling: false,
            phoneNumberIsValid: true,
            isPhoneValidatingRequestFinished: true
        };
    }

    componentDidMount() {
        let history = createBrowserHistory();
        this.waitForUserContext()
            .then(() => {
                const fetchData = () => {
                    if (this.context.user?.role === "PARENT" || this.context.user?.role === "EGG_DONOR") {
                        this.setState({
                            questions: [{}]
                        });
                        this.fetchDynamicGroups();
                    } else {
                        this.fetchStandardData(history);
                    }
                };

                if (!this.context.isSurveyFinished()) {
                    this.setSurveyLocation().then(fetchData);
                } else {
                    fetchData();
                }
            })
            .catch((error) => {
                console.error("Error waiting for user context:", error);
            });
    }

    waitForUserContext = () => {
        return new Promise((resolve) => {
            const checkContext = () => {
                if (this.context.user?.role) {
                    resolve();
                } else {
                    setTimeout(checkContext, 100);
                }
            };
            checkContext();
        });
    };

    fetchStandardData = (history) => {
        const currentSubSurvey = surveyQuestionMethods.isSurrogateAccountType() && !this.context?.isSurveyFinished() ? 1 : 0;
        SalesforceFieldService.retrieveSpecialSfdcFieldNames()
            .then(() => AppService.getQuestions(history, currentSubSurvey))
            .then((val) => {
                if (val) {
                    const sectionCount = val.updatedSubSurveysSections?.length ?? 0;
                    const lastSectionIndex = Math.max(0, sectionCount - 1);
                    const safeCurrentSubSurvey = Math.min(currentSubSurvey, lastSectionIndex);
                    const surrogateSkipFirstDone =
                        surveyQuestionMethods.isSurrogateAccountType() && !this.context?.isSurveyFinished() && safeCurrentSubSurvey > 0;
                    this.setState({
                        currentSubSurvey: safeCurrentSubSurvey,
                        finishedSurveys: surrogateSkipFirstDone ? [0] : [],
                        questions: val.updatedSubSurveysSections[safeCurrentSubSurvey],
                        allSubSurveySections: val.updatedSubSurveysSections,
                        subSurveys: val.subSurveys,
                        showSurveys: [UserSurveyStatus.COMPLETED, UserSurveyStatus.REJECTED, UserSurveyStatus.INITIAL_REJECTED].includes(
                            val.userSurveyStatus
                        )
                    });
                }
            })
            .then(() => {
                if (this.context.user?.role !== "PARENT" && this.context.user?.role !== "EGG_DONOR" && this.context.user.role !== "ADMIN") {
                    this.validatePhoneForSurrogateOrMilkDonorUsers();
                    this.fetchDynamicGroups();
                }
            });
    };

    componentDidUpdate(prevProps, prevState) {
        if (
            surveyQuestionMethods.isSurrogateAccountType() &&
            this.state.currentSubSurvey === 0 &&
            AppService.accountType !== prevState.accountType
        ) {
            this.setState({
                currentSubSurvey: 1,
                finishedSurveys: [0],
                accountType: AppService.accountType
            });
        }
    }

    validatePhoneForSurrogateOrMilkDonorUsers = async () => {
        const { questions } = this.state;
        if (questions) {
            const phoneNumberQuestion = questions[0].questions?.find((q) => q.type === "PHONE");
            const phoneValue = phoneNumberQuestion.answers[0]?.userAnswer;

            if (!phoneValue || phoneValue?.length === 0) return;

            this.toggleLoadingValidation(false);
            try {
                const response = await validatePhoneNumber(phoneValue);
                if (response.status === 200) {
                    this.toggleValidationPhoneNumber(true);
                }
            } catch (err) {
                if (err.response.data.code && err.response.data.code === 400) {
                    const message = err.response.data.message;
                    if (message === "Current phone is not valid US phone number") {
                        localStorage.setItem("current-phone", phoneValue);
                        this.toggleValidationPhoneNumber(false);
                    }
                } else {
                    this.toggleValidationPhoneNumber(true);
                }
            } finally {
                this.toggleLoadingValidation(true);
            }
        }
    };
    fetchDynamicGroups = async () => {
        const response = await QuestionListService.getDynamicGroups(this.context.user?.id);
        response.data.forEach((group) => {
            group && this.setDynamicQuestions({ ...group, createdInstances: JSON.parse(group.createdInstances) });
        });
    };

    setSurveyLocation = async () => {
        const response = await getSurveyLocation();
        const location = response.data;
        if (location.indexesActive) {
            const { subSurveyIndex, sectionIndex, questionIndex } = location;
            this.setState({
                currentSubSurvey: subSurveyIndex,
                lastSectionPassed: sectionIndex,
                questionIndex: questionIndex,
                indexesActive: location.indexesActive
            });
            location.subSurveyIndex > 0 && this.setFinishedSurveysByIndex(subSurveyIndex);
        } else {
            await this.updateSurveyLocation(this.state.currentSubSurvey, this.state.lastSectionPassed);
        }
    };

    setFinishedSurveysByIndex(index) {
        const finishedSurveys = [];
        for (let i = 0; i < index; i++) {
            finishedSurveys.push(i);
        }
        this.setState({ finishedSurveys });
    }

    saveQuestionOrResetLocation = async (params) => {
        if (this.context.isSurveyFinished()) {
            await this.resetSurveyOnFinish();
            return;
        }

        let firstUnansweredQuestionIndex;

        if (surveyQuestionMethods.isEdOrIpAccountType() && params) {
            const { subSurveyIndex, sectionIndex, selectedTopic } = params;
            firstUnansweredQuestionIndex = this.getFirstUnansweredQuestionIndex(selectedTopic);
            return await this.updateSurveyLocation(subSurveyIndex, sectionIndex, firstUnansweredQuestionIndex);
        }

        const { questions, lastSectionPassed, currentSubSurvey } = this.state;
        firstUnansweredQuestionIndex = this.getFirstUnansweredQuestionIndex(questions[lastSectionPassed]);
        return await this.updateSurveyLocation(currentSubSurvey, lastSectionPassed, firstUnansweredQuestionIndex);
    };

    updateSurveyLocation = async (subSurveyIndex = 0, sectionIndex = 0, questionIndex = 0) => {
        if (this.context.isSurveyFinished()) {
            return;
        }
        this.setState({
            lastSectionPassed: sectionIndex,
            questionIndex: questionIndex,
            currentSubSurvey: subSurveyIndex
        });
        this.setFinishedSurveysByIndex(subSurveyIndex);
        return await saveSurveyLocation(subSurveyIndex, sectionIndex, questionIndex);
    };

    getFirstUnansweredQuestionIndex = (topic) => {
        if (topic) {
            const firstUnansweredQuestionId = unansweredQuestions.getFirstUnansweredQuestion(topic.id || topic.sequenceId, true);
            return topic.questions?.findIndex((question) => this.isQuestionUnanswered(question, firstUnansweredQuestionId));
        }
    };

    isQuestionUnanswered = (question, firstUnansweredQuestionId) => {
        if (question.sequenceId === firstUnansweredQuestionId) {
            return true;
        }
        if (question?.type === "GROUP") {
            return question.questions.some((gq) => gq.sequenceId === firstUnansweredQuestionId);
        }
        return false;
    };

    changeSubSurvey = async (index) => {
        AppService.sectionChanged = true;
        if (this.context.isSurveyFinished()) {
            this.setState({
                questions: this.state.subSurveys[index].sections,
                currentSubSurvey: index
            });
            return;
        }
        await this.setState({
            questions: this.state.allSubSurveySections[index],
            currentSubSurvey: index
        });
        AppService.parsedQuestions = this.state.allSubSurveySections[index];
    };

    addCurrentSurveyToFinished = (index) => {
        const newFinishedSurveys = [...this.state.finishedSurveys];
        newFinishedSurveys.push(index);
        this.setState({ finishedSurveys: newFinishedSurveys });
        this.updateSurveyLocation(index + 1, 0);
    };

    toggleShowSurveys = () => {
        this.setState({ showSurveys: !this.state.showSurveys });
    };

    resetSurveyOnFinish = async () => {
        const savedUser = await resetSurveyLocation();
        this.context.setPortalStatus(savedUser.data.portalStatus);
    };

    resetFinishedSurveys = () => {
        this.setState({ finishedSurveys: [] });
    };

    resetSurveyLocation = () => {
        this.setState({ currentSubSurvey: 0, lastSectionPassed: 0 });
    };

    isSurveyLoad = (boolean) => {
        this.setState({ isSurveyLoaded: boolean });
    };

    setSurveyScroll = (boolean) => {
        this.setState({ stopScrolling: boolean });
    };

    setDynamicQuestions = (dynamicQuestion) => {
        this.setState((prevState) => {
            const { dynamicQuestions } = prevState;

            const existingQuestion = dynamicQuestions.find((question) => question.id === dynamicQuestion.id);

            if (existingQuestion) {
                return {
                    dynamicQuestions: dynamicQuestions.map((question) => (question.id === dynamicQuestion.id ? dynamicQuestion : question))
                };
            } else {
                return {
                    dynamicQuestions: [...dynamicQuestions, dynamicQuestion]
                };
            }
        });
    };

    updateQuestions = (questions) => {
        this.setState({ questions: questions });
    };

    toggleValidationPhoneNumber = (boolean) => {
        this.setState({ phoneNumberIsValid: boolean });
    };
    toggleLoadingValidation = (boolean) => {
        this.setState({ isPhoneValidatingRequestFinished: boolean });
    };

    render() {
        return (
            <SurveyQuestionsContext.Provider
                value={{
                    ...this.state,
                    changeSubSurvey: this.changeSubSurvey,
                    addCurrentSurveyToFinished: this.addCurrentSurveyToFinished,
                    toggleShowSurveys: this.toggleShowSurveys,
                    resetFinishedSurveys: this.resetFinishedSurveys,
                    updateSurveyLocation: this.updateSurveyLocation,
                    setSurveyLocation: this.setSurveyLocation,
                    saveQuestionOrResetLocation: this.saveQuestionOrResetLocation,
                    isSurveyLoad: this.isSurveyLoad,
                    setSurveyScroll: this.setSurveyScroll,
                    setDynamicQuestions: this.setDynamicQuestions,
                    resetSurveyLocation: this.resetSurveyLocation,
                    setFinishedSurveysByIndex: this.setFinishedSurveysByIndex,
                    updateQuestions: this.updateQuestions,
                    changeTopic: this.changeTopic,
                    toggleValidationPhoneNumber: this.toggleValidationPhoneNumber,
                    toggleLoadingValidation: this.toggleLoadingValidation
                }}
            >
                {this.props.children}
            </SurveyQuestionsContext.Provider>
        );
    }
}

export default SurveyQuestionsStore;
